import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { useSnackbar } from "notistack";
import React from "react";
import { assertNever } from "../../../utils.js";
import {
  useGenerateQuoteContractingPdfLazyQuery,
  useGenerateQuoteGeneratorPdfLazyQuery,
  useGenerateQuotePdfLazyQuery,
  useGenerateQuoteSexyPdfLazyQuery,
} from "./useDownloadQuotePdf.generated.js";

export type PdfDownloadArgs =
  | { templateType: "none"; calculationType: "agreed" | "proposed" }
  | {
      templateType: "contracting";
      calculationType: "agreed" | "proposed";
      contractingPdfId: string;
    }
  | {
      templateType: "sexy";
      calculationType: "proposed";
      pdfTemplateUrl: string;
    }
  | {
      templateType: "generator";
      calculationType: "agreed" | "proposed";
      pdfGeneratorTemplateId: string;
    };

export function useDownloadQuotePdf({
  projectId,
  docId,
}: {
  projectId: string;
  docId: string;
}) {
  const [loadingPdf, setLoadingPdf] = React.useState<null | string>(null);
  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();
  const [fetch] = useGenerateQuotePdfLazyQuery({ client });
  const [fetchContracting] = useGenerateQuoteContractingPdfLazyQuery({
    client,
  });
  const [fetchSexy] = useGenerateQuoteSexyPdfLazyQuery({
    client,
  });
  const [fetchGenerator] = useGenerateQuoteGeneratorPdfLazyQuery({
    client,
  });

  const handleDownload = async (args: PdfDownloadArgs) => {
    if (loadingPdf) return;

    try {
      switch (args.templateType) {
        case "none": {
          const { templateType, calculationType } = args;
          const key = `${templateType}-${calculationType}`;
          setLoadingPdf(key);
          const { data } = await fetch({
            variables: {
              projectId,
              quoteId: docId,
              calculationType,
            },
          });
          const url = getDataOrNull(data?.quoteGeneratePdf)?.url;
          if (!url) throw new Error("Failed to generate PDF");
          return url;
        }
        case "contracting": {
          const { templateType, calculationType, contractingPdfId } = args;
          const key = `${templateType}-${calculationType}-${contractingPdfId}`;
          setLoadingPdf(key);
          const { data } = await fetchContracting({
            variables: {
              projectId,
              quoteId: docId,
              contractingPdfId,
              calculationType,
            },
          });
          const url = getDataOrNull(data?.quoteGenerateContractingPdf)?.url;
          if (!url) throw new Error("Failed to generate PDF");
          return url;
        }
        case "sexy": {
          const { templateType, calculationType, pdfTemplateUrl } = args;
          const key = `${templateType}-${calculationType}-${pdfTemplateUrl}`;
          setLoadingPdf(key);
          const { data } = await fetchSexy({
            variables: {
              projectId,
              quoteId: docId,
              pdfTemplateUrl,
            },
          });
          const url = getDataOrNull(data?.quoteGenerateSexyPdf)?.url;
          if (!url) throw new Error("Failed to generate PDF");
          return url;
        }
        case "generator": {
          const { templateType, calculationType, pdfGeneratorTemplateId } =
            args;
          const key = `${templateType}-${calculationType}-${pdfGeneratorTemplateId}`;
          setLoadingPdf(key);
          const { data } = await fetchGenerator({
            variables: {
              projectId,
              quoteId: docId,
              pdfGeneratorTemplateId,
              calculationType,
            },
          });
          const url = getDataOrNull(data?.quoteGenerateGeneratorPdf)?.url;
          if (!url) throw new Error("Failed to generate PDF");
          return url;
        }
        default:
          assertNever(args);
      }
    } catch (error) {
      if (error instanceof Error)
        enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      setLoadingPdf(null);
    }
  };

  return { handleDownload, loadingPdf };
}
